export interface DifficultyConfig {
  // --- Wave Settings ---
  /** Duration of a normal wave before boss spawn (seconds) */
  waveDuration: number;
  /** Difficulty multiplier added per boss defeated */
  bossDifficultyFactor: number;
  /** Difficulty multiplier added per wave (excluding boss waves) */
  waveDifficultyFactor: number;

  // --- Player Settings ---
  /** Starting lives for the player */
  startingLives: number;
  /** Starting HP for the player */
  startingHp: number;
  /** Maximum HP the player can have */
  maxHpCap: number;
  /** Invulnerability duration after getting hit (ms) */
  invulnerabilityMs: number;
  /** Number of chomps needed to activate Godzilla mode */
  chompsForGodzilla: number;
  /** Chomp cooldown (ms) */
  chompCooldownMs: number;
  /** Beam full charge time (ms) */
  beamChargeMs: number;

  // --- Penalty & Upgrade Settings ---
  /** When do we count a penalty? "hit" means on every damage taken, "life" means only when a life is lost. "none" disables upgrade loss completely. */
  upgradeLossCondition: "hit" | "life" | "none";
  /** Number of penalties taken before the player loses an upgrade. Set to 1 to lose on every penalty, 2 for every other penalty, etc. */
  hitsBeforeUpgradeLoss: number;
  /** How many upgrades are lost when the penalty triggers */
  upgradesLostPerPenalty: number;
  /** Multiplier for the minimum upgrade floor (bosses defeated * this value) */
  upgradeFloorMultiplier: number;
  /** Enemies killed required to drop a non-boss upgrade */
  killsPerUpgradeDrop: number;

  // --- Max HP Increase Settings ---
  /** Condition to grant player more max HP */
  maxHpIncreaseCondition: "boss" | "kills" | "none";
  /** How much max HP to grant when the condition is met */
  maxHpIncreaseAmount: number;
  /** If condition is "kills", how many normal enemies killed are required to grant max HP */
  killsPerMaxHpIncrease: number;

  // --- Boss Settings ---
  /** Base HP of the first boss. Boss HP scales using the phase multiplier and global difficulty factor. */
  bossBaseHp: number;

  // --- Enemy Settings ---
  /** Base HP of a normal enemy. Normal Enemy HP scales using the phase multiplier and global difficulty factor. */
  enemyBaseHp: number;

  // --- Shooter Settings ---
  /** The wave at which shooting enemies start appearing */
  shootingEnemiesStartWave: number;
  /** How many shooting enemies spawn on the starting wave */
  shootingEnemiesBaseCount: number;
  /** How many extra shooting enemies spawn per wave after the start wave */
  shootingEnemiesPerWave: number;
  /** Maximum number of shooting enemies allowed in a single wave */
  shootingEnemiesMaxCount: number;

  // --- Score Settings ---
  /** Score multiplier increase per wave */
  scoreMultiplierPerWave: number;
  /** Score bonus for completing the game */
  victoryBonus: number;

  // --- Upgrade Drops ---
  /** Whether a boss drops an upgrade when killed */
  bossDropsUpgrade: boolean;
  /** Number of upgrades the boss drops (if bossDropsUpgrade is true) */
  bossUpgradeDropCount: number;

  // --- Upgrade Specific Stats ---
  upgrades: {
    MULTISHOT: {
      maxLevel: number;
    };
    SPREAD: {
      maxLevel: number;
      /** Spread angle increment per level */
      anglePerLevel: number;
    };
    FIRE_RATE: {
      maxLevel: number;
      /** Array mapping level -> cooldown in ms (e.g. [600, 450, 320, 200, 100, 45]) */
      cooldownsMs: number[];
    };
    PIERCING: {
      maxLevel: number;
      /** Number of extra enemies a bullet can pierce per level */
      extraHitsPerLevel: number;
    };
    SPEED: {
      maxLevel: number;
      /** Array mapping level -> movement speed multiplier (e.g. [1.0, 1.15, 1.35, 1.6]) */
      speedMultipliers: number[];
    };
    SHOT_POWER: {
      maxLevel: number;
      /** Bonus multiplier added to shot damage per level (e.g., 0.5) */
      damageBonusPerLevel: number;
    };
  };
}

export const DefaultDifficulty: DifficultyConfig = {
  waveDuration: 25,
  bossDifficultyFactor: 0.15,
  waveDifficultyFactor: 0.05,
  startingLives: 3,
  startingHp: 3,
  maxHpCap: 5,
  invulnerabilityMs: 500,
  chompsForGodzilla: 10,
  chompCooldownMs: 2500,
  beamChargeMs: 3000,
  upgradeLossCondition: "hit",
  hitsBeforeUpgradeLoss: 2,
  upgradesLostPerPenalty: 1,
  upgradeFloorMultiplier: 1,
  killsPerUpgradeDrop: 40,
  maxHpIncreaseCondition: "boss",
  maxHpIncreaseAmount: 1,
  killsPerMaxHpIncrease: 100,
  bossBaseHp: 60,
  enemyBaseHp: 1,
  shootingEnemiesStartWave: 5,
  shootingEnemiesBaseCount: 3,
  shootingEnemiesPerWave: 1,
  shootingEnemiesMaxCount: 10,
  scoreMultiplierPerWave: 0.20,
  victoryBonus: 250000,
  bossDropsUpgrade: true,
  bossUpgradeDropCount: 1,
  upgrades: {
    MULTISHOT: { maxLevel: 5 },
    SPREAD: { maxLevel: 3, anglePerLevel: 0.12 },
    FIRE_RATE: { maxLevel: 5, cooldownsMs: [600, 450, 320, 200, 100, 45] },
    PIERCING: { maxLevel: 3, extraHitsPerLevel: 1 },
    SPEED: { maxLevel: 3, speedMultipliers: [1.0, 1.15, 1.35, 1.6] },
    SHOT_POWER: { maxLevel: 13, damageBonusPerLevel: 0.5 },
  }
};

export const HardDifficulty: DifficultyConfig = {
  waveDuration: 20, // Faster waves
  bossDifficultyFactor: 0.25, // Bosses scale faster
  waveDifficultyFactor: 0.10, // Waves scale faster
  startingLives: 2, // Less starting lives
  startingHp: 2, // Less starting HP
  maxHpCap: 4,
  invulnerabilityMs: 300, // Less invulnerability time
  chompsForGodzilla: 12, // Takes longer to charge godzilla
  chompCooldownMs: 3000, // Longer chomp cooldown
  beamChargeMs: 4000, // Longer beam charge
  upgradeLossCondition: "hit",
  hitsBeforeUpgradeLoss: 1, // Lose an upgrade on every hit!
  upgradesLostPerPenalty: 1,
  upgradeFloorMultiplier: 0.5, // Upgrade floor rises slower
  killsPerUpgradeDrop: 50, // Less upgrades from enemies
  maxHpIncreaseCondition: "boss",
  maxHpIncreaseAmount: 1,
  killsPerMaxHpIncrease: 150,
  bossBaseHp: 100,
  enemyBaseHp: 2,
  shootingEnemiesStartWave: 3,
  shootingEnemiesBaseCount: 4,
  shootingEnemiesPerWave: 2,
  shootingEnemiesMaxCount: 15,
  scoreMultiplierPerWave: 0.30, // Better score scaling as reward
  victoryBonus: 500000,
  bossDropsUpgrade: true,
  bossUpgradeDropCount: 1,
  upgrades: {
    MULTISHOT: { maxLevel: 5 },
    SPREAD: { maxLevel: 3, anglePerLevel: 0.12 },
    FIRE_RATE: { maxLevel: 5, cooldownsMs: [600, 450, 320, 200, 100, 45] },
    PIERCING: { maxLevel: 3, extraHitsPerLevel: 1 },
    SPEED: { maxLevel: 3, speedMultipliers: [1.0, 1.15, 1.35, 1.6] },
    SHOT_POWER: { maxLevel: 13, damageBonusPerLevel: 0.5 },
  }
};

export const EasyDifficulty: DifficultyConfig = {
  waveDuration: 30, // Slower waves
  bossDifficultyFactor: 0.10, // Bosses scale slower
  waveDifficultyFactor: 0.02, // Waves scale slower
  startingLives: 5, // More starting lives
  startingHp: 5, // More starting HP
  maxHpCap: 8,
  invulnerabilityMs: 1000, // More invulnerability time
  chompsForGodzilla: 6, // Quick godzilla charge
  chompCooldownMs: 1500, // Faster chomp
  beamChargeMs: 2000, // Faster beam charge
  upgradeLossCondition: "life", // Only lose upgrades when you die completely
  hitsBeforeUpgradeLoss: 1, // Will trigger on 1 life loss
  upgradesLostPerPenalty: 1,
  upgradeFloorMultiplier: 2, // Generous upgrade floor
  killsPerUpgradeDrop: 25, // Plentiful upgrades
  maxHpIncreaseCondition: "boss",
  maxHpIncreaseAmount: 1,
  killsPerMaxHpIncrease: 50,
  bossBaseHp: 40,
  enemyBaseHp: 1,
  shootingEnemiesStartWave: 7,
  shootingEnemiesBaseCount: 2,
  shootingEnemiesPerWave: 1,
  shootingEnemiesMaxCount: 8,
  scoreMultiplierPerWave: 0.10, // Lower score scaling
  victoryBonus: 100000,
  bossDropsUpgrade: true,
  bossUpgradeDropCount: 2, // Extra upgrades on easy
  upgrades: {
    MULTISHOT: { maxLevel: 5 },
    SPREAD: { maxLevel: 3, anglePerLevel: 0.12 },
    FIRE_RATE: { maxLevel: 5, cooldownsMs: [600, 450, 320, 200, 100, 45] },
    PIERCING: { maxLevel: 3, extraHitsPerLevel: 1 },
    SPEED: { maxLevel: 3, speedMultipliers: [1.0, 1.15, 1.35, 1.6] },
    SHOT_POWER: { maxLevel: 13, damageBonusPerLevel: 0.5 },
  }
};

export let ACTIVE_DIFFICULTY: DifficultyConfig = DefaultDifficulty;

// Support dynamic loading of difficulty via URL query parameters
if (typeof window !== "undefined") {
  const params = new URLSearchParams(window.location.search);
  const diffParam = params.get("diff");
  if (diffParam === "hard") {
    ACTIVE_DIFFICULTY = HardDifficulty;
  } else if (diffParam === "easy") {
    ACTIVE_DIFFICULTY = EasyDifficulty;
  }
}

export function setDifficulty(config: DifficultyConfig) {
  ACTIVE_DIFFICULTY = config;
}
